import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
} from '@mui/material'
import { RailTrackResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { RailTrackDeselectDialog } from './RailTrackDeselectDialog'

interface Props {
  open: boolean
  onClose: () => void
  railVisitId: number
  onSave: (selectedTrackIds: string[], targetTrackId?: string | null) => Promise<void>
}

export const AssignRailTracksDialog: FC<Props> = observer(
  ({ open, onClose, railVisitId, onSave }) => {
    const { t } = useTranslate()
    const theme = useTheme()
    const { railTrackItemStore, railVisitItemStore, appViewStore } = usePlanningStore()

    const [reassignmentTargetId, setReassignmentTargetId] = useState<string | null>(null)
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [originalIds, setOriginalIds] = useState<string[]>([])

    const [trackToDeselect, setTrackToDeselect] = useState<RailTrackResponseDto | null>(null)

    useEffect(() => {
      if (open) {
        const visitItem = railVisitItemStore.getById(railVisitId)
        if (visitItem) {
          const currentIds = visitItem.railTracks.map(t => t.id)

          setSelectedIds(currentIds)
          setOriginalIds(currentIds)
        } else {
          setSelectedIds([])
          setOriginalIds([])
        }
      }
    }, [open, railVisitId, railTrackItemStore, railVisitItemStore])

    const allTracks = Object.values(railTrackItemStore.elements || {})
      .map(item => item.data)
      .filter(t => !t.isDeleted)

    const handleToggleTrack = (track: RailTrackResponseDto) => {
      const isSelected = selectedIds.includes(track.id)

      if (isSelected) {
        if (selectedIds.length === 1) {
          appViewStore.setShowAlert(
            'warning',
            t('atLeastOneTrack', 'At least one rail track must be assigned'),
          )
          return
        }

        const visitItem = railVisitItemStore.getById(railVisitId)
        const isOriginal = originalIds.includes(track.id)

        const uniquePositions = _.uniqBy(
          visitItem?.railcarTrackPositions || [],
          rtp => rtp.data.railcarId,
        )

        const positionsOnThisTrack = uniquePositions.filter(rtp => {
          const trackId = rtp.data?.railTrackId || rtp.railTrack?.id
          return trackId === track.id
        })

        const hasContent = positionsOnThisTrack.length > 0

        if (isOriginal && hasContent) {
          setTrackToDeselect(track)
        } else {
          setSelectedIds(prev => prev.filter(id => id !== track.id))
        }
      } else {
        setSelectedIds(prev => [...prev, track.id])
      }
    }

    const confirmDeselect = (targetTrackId: string | null) => {
      if (trackToDeselect) {
        setSelectedIds(prev => prev.filter(id => id !== trackToDeselect.id))
        setReassignmentTargetId(targetTrackId)
        setTrackToDeselect(null)
      }
    }

    const handleSave = async () => {
      await onSave(selectedIds, reassignmentTargetId)
      setReassignmentTargetId(null)
      onClose()
    }

    return (
      <>
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
          <DialogTitle>{t('assignRailTracks', 'Assign rail tracks')}</DialogTitle>

          <DialogContent>
            <Box sx={{ py: 2 }}>
              <Box display='flex' flexWrap='wrap' gap={1}>
                {allTracks.map(track => {
                  const isSelected = selectedIds.includes(track.id)
                  return (
                    <Chip
                      key={track.id}
                      label={track.name}
                      onClick={() => handleToggleTrack(track)}
                      color={isSelected ? 'default' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{
                        backgroundColor: isSelected ? theme.palette.grey[600] : 'transparent',
                        color: isSelected ? 'white' : 'text.primary',
                        border: isSelected ? 'none' : `1px solid ${theme.palette.divider}`,
                        '&:hover': {
                          backgroundColor: isSelected
                            ? theme.palette.grey[600]
                            : theme.palette.action.hover,
                        },
                      }}
                    />
                  )
                })}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleSave} variant='contained' color='primary'>
              {t('save', 'Save')}
            </Button>
            <Button onClick={onClose} color='inherit'>
              {t('cancel', 'Cancel')}
            </Button>
          </DialogActions>
        </Dialog>

        {trackToDeselect && (
          <RailTrackDeselectDialog
            open={true}
            availableTracks={allTracks.filter(
              t => selectedIds.includes(t.id) && t.id !== trackToDeselect.id,
            )}
            trackName={trackToDeselect.name}
            onConfirm={confirmDeselect}
            onCancel={() => setTrackToDeselect(null)}
          />
        )}
      </>
    )
  },
)
